"use client"

import Link from "next/link"
import { ListingCard } from "@/components/marketing/content/listing-card"
import { ListingGrid } from "@/components/marketing/content/listing-grid"
import { ListingPagination } from "@/components/marketing/content/listing-pagination"
import { track } from "@/lib/analytics/track"
import { buildListingHref, LISTING_PAGE_SIZE } from "@/lib/marketing/pagination"

interface Integration {
  slug: string
  title: string
  description: string
  category?: string
  logoUrl?: string
}

const BASE_PATH = "/integrations"

export function IntegrationsListing({
  integrations,
  categories,
  activeCategory,
  page,
  totalPages,
}: {
  integrations: Integration[]
  categories: string[]
  activeCategory: string
  page: number
  totalPages: number
}) {
  const pills = [
    { id: "all", label: "All" },
    ...categories.map((c) => ({ id: c, label: c })),
  ]

  return (
    <>
      {/* Category pills — real ?category= links; selecting one resets to page 1 */}
      <div className="flex flex-wrap gap-3">
        {pills.map((cat) => {
          const isActive = cat.id === activeCategory
          return (
            <Link
              key={cat.id}
              href={buildListingHref(BASE_PATH, { category: cat.id })}
              scroll={false}
              onClick={() => track("integrations_filter", { category: cat.id })}
              className={`rounded-xl border px-5 py-3 text-sm font-medium transition-all ${
                isActive
                  ? "border-foreground bg-foreground text-background shadow-md"
                  : "border-border bg-background text-muted-foreground hover:border-foreground/20 hover:bg-muted hover:text-foreground"
              }`}
            >
              {cat.label}
            </Link>
          )
        })}
      </div>

      {/* Integration grid */}
      <div className="mt-8">
        {integrations.length > 0 ? (
          <ListingGrid>
            {integrations.map((integration, index) => (
              <ListingCard
                key={integration.slug}
                href={`/integrations/${integration.slug}`}
                title={integration.title}
                description={integration.description}
                category={integration.category}
                logo={integration.logoUrl}
                onClick={() =>
                  track("integrations_card_click", {
                    slug: integration.slug,
                    position: (page - 1) * LISTING_PAGE_SIZE + index + 1,
                  })
                }
              />
            ))}
          </ListingGrid>
        ) : (
          <div className="py-16 text-center">
            <p className="text-muted-foreground text-lg">
              No integrations found.{" "}
              <Link
                href={BASE_PATH}
                className="text-foreground underline underline-offset-4 hover:no-underline"
              >
                Clear filter
              </Link>
            </p>
          </div>
        )}
      </div>

      <ListingPagination
        basePath={BASE_PATH}
        page={page}
        totalPages={totalPages}
        category={activeCategory}
        showPageJump
      />
    </>
  )
}
